// Small text and address helpers shared by the "Red flag warnings" tab of Mailer Setup.
//
// Kept apart from the components so the rules that are easy to get subtly wrong — what
// counts as an address outside the company, how a red-flag number is spoken — are in one
// place and tested on their own. The server remains the authority on every address and
// every message text; these only shape what the screen says before the save.

/** 1 → "1st", 2 → "2nd", 11 → "11th", 23 → "23rd". */
export const ordinal = (n: number): string => {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const last = n % 10;
  return `${n}${last === 1 ? 'st' : last === 2 ? 'nd' : last === 3 ? 'rd' : 'th'}`;
};

/** "3rd red flag" — how a message's number is named everywhere on the tab. */
export const flagNumberLabel = (n: number): string => `${ordinal(n)} red flag`;

/** Said under every preview: what the reader is looking at is not a real employee's mail. */
export const SAMPLE_NOTE =
  'Shown with sample data. The red flag details under the message are added by the system.';

/** Splits a typed list on commas, semicolons and spaces; drops repeats (case-insensitive). */
export const parseAddresses = (raw: string): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\s,;]+/)) {
    const address = part.trim();
    if (!address || seen.has(address.toLowerCase())) continue;
    seen.add(address.toLowerCase());
    out.push(address);
  }
  return out;
};

/** A first check only — one name@domain.tld with no spaces or list characters. */
export const looksLikeAddress = (value: string): boolean =>
  /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:".]+$/.test(value.trim());

const COMPANY_DOMAIN = 'channelplay.in';

/**
 * Outside the company = the domain is neither channelplay.in nor one of its sub-domains.
 * Compared on the domain, not the tail of the string: `x@notchannelplay.in` is outside,
 * `x@team.channelplay.in` is inside.
 */
export const isOutsideCompany = (address: string): boolean => {
  const domain = address.trim().toLowerCase().split('@').pop() ?? '';
  return !(domain === COMPANY_DOMAIN || domain.endsWith(`.${COMPANY_DOMAIN}`));
};

/** The server's own message when it sent one (they are written for the screen), else `fallback`. */
export const apiMessage = (e: unknown, fallback: string): string => {
  const message = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

export const httpStatus = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

/** A moment as "08 Oct 2026, 10:02 am" in India time; anything unreadable is shown as it came. */
export const formatMoment = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};
